import _ from 'lodash'
import { actions } from './reducer'
import ssapi from 'lib/api/secondstepApi'
import { push } from 'connected-react-router'
import { addNotification } from 'store/notifications'
import { AxiosResponse } from 'axios'
import { Site, SiteFilters, SiteUpdate } from 'types'

export const fetchSite = siteId => {
  return async dispatch => {
    dispatch(actions.fetchingSites())
    try {
      const response: AxiosResponse<Site> = await ssapi.getSite(siteId)
      dispatch(actions.fetchingSitesSuccess([response.data]))
    } catch (error) {
      dispatch(actions.fetchingSitesError(error))
    }
  }
}

export const fetchSites = (criteria: SiteFilters) => {
  return async dispatch => {
    dispatch(actions.fetchingSites())
    dispatch(actions.setSiteSearchCriteria(criteria))
    try {
      const response: AxiosResponse<Site[]> = await ssapi.getSites(criteria)
      dispatch(actions.fetchingSitesSuccess(response.data))
      if (response.data.length === 1)
        dispatch(push(`/css-admin/sites/${response.data[0].id}`))
    } catch (error) {
      dispatch(actions.fetchingSitesError(error))
    }
  }
}

export const mergeSite = mergeSiteParameters => {
  return async function (dispatch) {
    const { siteId, primarySiteId, isPreview } = mergeSiteParameters
    dispatch(actions.mergingSite())
    try {
      const response = await ssapi.mergeSite({
        siteId,
        primarySiteId,
        isPreview,
      })

      if (!isPreview) {
        dispatch(
          addNotification({
            color: 'success',
            text: `You have sucessfully merged Site ${siteId} into Site ${primarySiteId}`,
          }),
        )
      }
      const data = response.data !== '' ? response.data : null
      if (data) {
        data.isPreview = isPreview
        dispatch(actions.mergingSiteSuccess(data))
      } else {
        dispatch(actions.mergingSiteSuccess({ isPreview }))
      }
    } catch (error) {
      const { response, message } = error || {}
      const { data } = response || {}
      const errorMessage = data || message || 'An error occurred during merge'
      dispatch(
        addNotification({
          color: 'danger',
          text: errorMessage,
        }),
      )
      dispatch(actions.mergingSiteError(errorMessage))
    }
  }
}

export const updateSite = (site: SiteUpdate) => {
  return async dispatch => {
    dispatch(actions.updatingSite())
    try {
      const response: AxiosResponse<Site> = await ssapi.updateSite(site)
      dispatch(actions.updatingSiteSuccess(response.data))
      dispatch(
        addNotification({
          color: 'success',
          text: 'Site successfully updated',
        }),
      )
      // need to fetch site again after update because the PUT call returns no data
      dispatch(fetchSites({ siteId: site.id, includeFutureLicenses: true }))
    } catch (error) {
      const { response } = error
      const message = response.data || error.message
      dispatch(
        addNotification({
          color: 'danger',
          text: `An Error has occured during update: ${message}`,
        }),
      )
      dispatch(actions.updatingSiteError(error))
    }
  }
}

export const fetchSiteUsers = siteId => {
  return async (dispatch, getState) => {
    const state = getState()
    const siteUsersData = state.sitesManager.siteUsers.data
    if (siteUsersData && siteUsersData[siteId]) return
    dispatch(actions.fetchingSiteUsers())

    try {
      const response = await ssapi.getSiteUsers(siteId)
      dispatch(
        actions.fetchingSiteUsersSuccess({ siteId, siteUsers: response.data }),
      )
    } catch (error) {
      dispatch(actions.fetchingSiteUsersError(error))
    }
  }
}

export const createSiteUser = (siteUser, sendEmail = true, selectedSite) => {
  return async dispatch => {
    dispatch(actions.creatingSiteUser())

    try {
      const response = await ssapi.createSiteUser(siteUser, sendEmail)
      const newSiteUser = response.data
      dispatch(
        addNotification({
          color: 'success',
          text: sendEmail
            ? `${newSiteUser.inviteEmail} was added to ${selectedSite.name} and was sent an email notification`
            : `${newSiteUser.inviteEmail} was added to ${selectedSite.name}`,
        }),
      )
      dispatch(actions.creatingSiteUserSuccess(response.data))
    } catch (error) {
      dispatch(
        addNotification({
          color: 'info',
          text: `The user ${siteUser.inviteEmail} is already a member of this site.`,
        }),
      )
      dispatch(actions.creatingSiteUserError(error))
    }
  }
}

export const createSiteUsers = (siteId, siteUsers, sendEmail) => {
  return async dispatch => {
    dispatch(actions.creatingSiteUsers())

    try {
      const response = await ssapi.bulkCreateSiteUsers(
        siteId,
        siteUsers,
        sendEmail,
      )
      const newSiteUsers = response.data.createdItems
      const failedSiteUsers = response.data.failedItems
      const invalidSiteUsers = response.data.invalidItems
      dispatch(
        actions.creatingSiteUsersSuccess({
          siteId,
          newSiteUsers,
          failedSiteUsers,
          invalidSiteUsers,
          sendEmail,
        }),
      )
    } catch (error) {
      dispatch(actions.creatingSiteUsersError(error))
    }
  }
}

export const updateSiteUser = (siteUser, notification) => {
  return async dispatch => {
    dispatch(actions.updatingSiteUser())
    const msgParams = {
      role: siteUser.roles[0],
      email: siteUser.inviteEmail,
    }

    try {
      await ssapi.updateSiteUser(siteUser)
      dispatch(actions.updatingSiteUserSuccess(siteUser))
      dispatch(
        addNotification({
          color: 'success',
          text: notification.successMessage(msgParams),
        }),
      )
    } catch (error) {
      dispatch(actions.updatingSiteUserError(error))
      dispatch(
        addNotification({
          color: 'danger',
          text: notification.errorMessage(msgParams),
        }),
      )
    }
  }
}

export const remindSiteUser = siteUser => {
  return async dispatch => {
    dispatch(actions.remindingSiteUsers())

    try {
      await ssapi.remindSiteUser(siteUser)
      dispatch(
        addNotification({
          color: 'success',
          text: `You've sent a reminder to ${siteUser.inviteEmail}`,
        }),
      )
      dispatch(actions.remindingSiteUserSuccess())
    } catch (error) {
      dispatch(
        addNotification({
          color: 'danger',
          text: `We weren't able to remind ${siteUser.inviteEmail}. Please try again later.`,
        }),
      )
      dispatch(actions.remindingSiteUserError(error))
    }
  }
}

export const remindSiteUsers = (siteId, emailFilters) => {
  return async dispatch => {
    dispatch(actions.remindingSiteUsers())

    try {
      await ssapi.remindSiteUsers(siteId, emailFilters)
      // TODO: Should we be calculating the total affect users, or should the API return the number of emails it sent?
      dispatch(
        addNotification({
          color: 'success',
          text: `Bulk notifications sent`,
        }),
      )
      dispatch(actions.remindingSiteUsersSuccess())
    } catch (error) {
      dispatch(
        addNotification({
          color: 'danger',
          text: `Something went wrong: ${error.message}`,
        }),
      )
      dispatch(actions.remindingSiteUsersError(error))
    }
  }
}

export const resetBulkAddUsers = () => {
  return async dispatch => {
    dispatch(actions.resetBulkAddUsers())
  }
}

export const bulkAddExternalSiteMapping = (file: File) => {
  return async function (dispatch) {
    dispatch(actions.bulkAddingExternalSiteMapping())
    const bodyFormData = new FormData()
    bodyFormData.set('externalSiteMaps', file)

    try {
      const response = await ssapi.bulkAddExternalSiteMapping(bodyFormData)
      dispatch(actions.bulkAddingExternalSiteMappingSuccess(response.data))
    } catch (error) {
      const errorMessage = error.response.data || error.message
      dispatch(actions.bulkAddingExternalSiteMappingError(errorMessage))
      dispatch(
        addNotification({
          color: 'danger',
          text: errorMessage,
        }),
      )
    }
  }
}

export const clearSites = () => {
  return async dispatch => {
    dispatch(actions.clearSites())
  }
}

export const siteOrgNetSuiteCustomerSearch = ({ nsCustomerId, orgId }) => {
  return new Promise((resolve, reject) =>
    siteOrgNetSuiteCustomerSearchDebounced({
      nsCustomerId,
      orgId,
      resolve,
      reject,
    }),
  )
}

const siteOrgNetSuiteCustomerSearchDebounced = _.debounce(
  async ({ nsCustomerId, orgId, resolve, reject }) => {
    try {
      const response = await ssapi.siteOrgNetSuiteCustomerSearch(
        nsCustomerId,
        orgId,
      )
      resolve(response)
    } catch (err) {
      reject(err)
    }
  },
  300,
)

export default {
  clearSites,
  createSiteUser,
  createSiteUsers,
  fetchSite,
  fetchSites,
  fetchSiteUsers,
  mergeSite,
  updateSite,
  updateSiteUser,
  remindSiteUser,
  remindSiteUsers,
  resetBulkAddUsers,
}
